import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  GlobeIcon,
  SewingPinIcon,
  VideoIcon,
} from "@radix-ui/react-icons";
import {
  ApiError,
  AvailabilitiesService,
  AvailabilityMessage,
  MeetingsService,
  OrganizationMeetingLocationType,
  OrganizationPublicResponse,
  OrganizationWebPagePublicResponse,
  PublicBookingLocation,
  TutorPublicResponse,
  TutorWebPageResponse,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { inEmbed, revealTop } from "util/embed";
import { safePhoto } from "util/safePhoto";
import { CURRENT_TIMEZONE } from "util/timezones";
import BookingDone, { BookingSummary, FactRow, whenLines } from "./BookingDone";
import PhoneVerifyStep from "./PhoneVerifyStep";
import { OrgLogo, PersonPhoto, PublicShell } from "./PublicShell";
import { tutorSubjects } from "./PublicOrgPage";
import MonthGrid from "./MonthGrid";
import WhoStep, { Who } from "./WhoStep";
import ZonePicker from "./ZonePicker";
import {
  lengthLabel,
  loadVerified,
  orgHref,
  saveVerified,
  timeLabel,
  tint,
  Verified,
  zoneLabel,
} from "./publicBooking";

const placeLabel = (place?: PublicBookingLocation) =>
  !place
    ? "Tutor will confirm the place"
    : [place.name, place.city].filter(Boolean).join(", ");

/**
 * One choice of a few (Length, Where): chips that wrap whole. They are radio
 * buttons, so Tab reaches the group, the arrow keys move within it, and a tap
 * anywhere on a chip picks it. Each radio sits beside its label, not inside
 * it: the app styles `label > input[type="radio"]` as its own dotted radio.
 */
function ChoiceChips<T extends string | number>({
  label,
  name,
  options,
  value,
  onChange,
  accent,
}: {
  label: string;
  name: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  accent: string;
}) {
  return (
    <div className="mt-4">
      <p id={`${name}-label`} className="text-xs font-semibold text-gray-600">
        {label}
      </p>
      <div
        role="radiogroup"
        aria-labelledby={`${name}-label`}
        className="mt-1.5 flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const on = option.value === value;
          const id = `${name}-${option.value}`;
          return (
            <div key={option.value}>
              <input
                id={id}
                type="radio"
                name={name}
                value={option.value}
                checked={on}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <label
                htmlFor={id}
                className="block cursor-pointer rounded-lg border px-3.5 py-2 text-sm font-medium peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2"
                style={{
                  outlineColor: accent,
                  ...(on
                    ? {
                        backgroundColor: accent,
                        borderColor: accent,
                        color: "white",
                      }
                    : { borderColor: "#D1D5DB", color: "#374151" }),
                }}
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A tutor's booking page, Calendly's layout: who on the left; the month and
 * the day's times on the right; then the phone code and who it is for; then
 * a done page. The step and the choices live in the URL, so Back works.
 */
export default function PublicTutorPage({
  page,
  org,
  tutor,
  tutorPage,
}: {
  page: OrganizationWebPagePublicResponse;
  org: OrganizationPublicResponse;
  tutor: TutorPublicResponse;
  tutorPage: TutorWebPageResponse;
}) {
  const booking = page.booking;
  const accent = page.primary_color;
  const tutorName = `${tutor.first_name} ${tutor.last_name}`;
  const subjects = tutorSubjects(tutor);
  const [params, setParams] = useSearchParams();

  // Choices, read from the URL and checked against what is offered.
  const subjectId =
    subjects.find((s) => s.id === Number(params.get("subject")))?.id ??
    subjects[0]?.id;
  const subject = subjects.find((s) => s.id === subjectId);
  const length =
    booking.families_choose_duration &&
    booking.duration_options.includes(Number(params.get("length")))
      ? Number(params.get("length"))
      : booking.families_choose_duration
      ? booking.duration_options.includes(booking.default_duration)
        ? booking.default_duration
        : booking.duration_options[0]
      : booking.default_duration;
  // The place the family picked (it is in the address), if any.
  const chosenPlace = booking.families_choose_location
    ? booking.locations.find((l) => l.id === Number(params.get("place")))
    : undefined;
  const step = params.get("step") ?? "time";
  const start = params.get("start") ?? undefined;

  const choose = (
    changes: Record<string, string | number | undefined>,
    push = false
  ) => {
    // In a widget, a new step starts at the widget's top.
    if ("step" in changes) revealTop();
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) =>
          value === undefined ? next.delete(key) : next.set(key, String(value))
        );
        return next;
      },
      { replace: !push }
    );
  };

  const [zone, setZone] = useState(CURRENT_TIMEZONE || booking.timezone);
  const [month, setMonth] = useState(() =>
    moment.tz(start ?? undefined, zone).format("YYYY-MM")
  );
  const [day, setDay] = useState<string | undefined>(
    start ? moment.tz(start, zone).format("YYYY-MM-DD") : undefined
  );
  const [picked, setPicked] = useState<string | undefined>(start);
  const [slots, setSlots] = useState<AvailabilityMessage[]>();
  const [notice, setNotice] = useState<string>();
  const [moreBio, setMoreBio] = useState(false);
  const [verified, setVerified] = useState<Verified | undefined>(() =>
    loadVerified(org.id)
  );
  // A new phone step (a new key) after "Use a different number" or an
  // expired code, with the number to start from.
  const [phoneStep, setPhoneStep] = useState({ key: 0, prefill: "" });
  const resetPhone = (prefill: string) => {
    saveVerified(org.id, undefined);
    setVerified(undefined);
    setPhoneStep((current) => ({ key: current.key + 1, prefill }));
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{
    status: "requested" | "booked";
    studentName: string;
  }>();

  // Only the latest request's answer is shown: a slow one for an earlier
  // month or length must not replace it.
  const latestSlots = useRef(0);
  const loadSlots = useCallback(() => {
    const ask = ++latestSlots.current;
    const from = moment.tz(`${month}-01`, zone);
    setSlots(undefined);
    AvailabilitiesService.getAvailabilitiesByTutorForPublicBookingPage({
      tutorId: tutor.id,
      start: moment.max(from, moment()).toISOString(),
      until: from.clone().add(1, "month").toISOString(),
      duration: booking.families_choose_duration ? length : undefined,
    })
      .then((open) => {
        if (ask === latestSlots.current) setSlots(open);
      })
      .catch(() => {
        if (ask === latestSlots.current) setSlots([]);
      });
  }, [tutor.id, month, zone, length, booking.families_choose_duration]);

  useEffect(loadSlots, [loadSlots]);

  // The offered places free at each open time (every offered place when
  // the server doesn't say).
  const freeAt = useMemo(
    () => new Map((slots ?? []).map((slot) => [slot.start, slot.location_ids])),
    [slots]
  );
  const freePlaces = (iso: string) => {
    const ids = freeAt.get(iso);
    return booking.locations.filter((l) => !ids || ids.includes(l.id));
  };
  // The times shown: the tutor is free, and so is the place the family
  // picked, or any offered place before they pick one.
  const times = useMemo(
    () =>
      slots
        ?.filter(
          (slot) =>
            !chosenPlace ||
            !slot.location_ids ||
            slot.location_ids.includes(chosenPlace.id)
        )
        .map((slot) => slot.start),
    [slots, chosenPlace]
  );
  // The place: the family's pick, else the first offered place free at the
  // picked time. With no pick and no time yet, none.
  const place = booking.families_choose_location
    ? chosenPlace ?? (picked ? freePlaces(picked)[0] : undefined)
    : booking.locations[0];
  // To the details, with the place in the address.
  const toDetails = (iso: string) =>
    choose(
      {
        step: "details",
        start: iso,
        place: booking.families_choose_location ? place?.id : undefined,
      },
      true
    );

  // The open times by day, in the visitor's zone.
  const byDay = useMemo(() => {
    const days = new Map<string, string[]>();
    (times ?? []).forEach((iso) => {
      const key = moment.tz(iso, zone).format("YYYY-MM-DD");
      days.set(key, [...(days.get(key) ?? []), iso]);
    });
    return days;
  }, [times, zone]);

  const firstMonth = moment.tz(zone).format("YYYY-MM");
  const lastMonth =
    booking.max_days_ahead != null
      ? moment.tz(zone).add(booking.max_days_ahead, "days").format("YYYY-MM")
      : undefined;
  const monthDate = moment(`${month}-01`).toDate();
  const shiftMonth = (by: number) => {
    setMonth(moment(`${month}-01`).add(by, "month").format("YYYY-MM"));
    setDay(undefined);
    setPicked(undefined);
  };

  const summary: BookingSummary | undefined = start
    ? {
        title: `${subject?.name ?? ""} with ${tutorName}`,
        tutorFirstName: tutor.first_name,
        tutorName,
        tutorPhoto: tutorPage.photo_url,
        start,
        minutes: length,
        place: placeLabel(place),
        zone,
      }
    : undefined;

  const book = (who: Who) => {
    if (!verified || !start || !subjectId) return;
    setBusy(true);
    setError(undefined);
    MeetingsService.createMeetingThroughPublicBookingPage({
      requestBody: {
        org_id: org.id,
        token: verified.token,
        tutor_id: tutor.id,
        org_subject_id: subjectId,
        start,
        duration: booking.families_choose_duration ? length : undefined,
        location_id: booking.families_choose_location ? place?.id : undefined,
        ...who,
      },
    })
      .then((result) => {
        const student =
          "student_id" in who
            ? verified.students.find((s) => s.id === who.student_id)
            : "for_self" in who
            ? who.for_self
            : who.new_student;
        // The family as it is now, for "Book another session".
        const refreshed = { ...verified, ...result.family };
        saveVerified(org.id, refreshed);
        setVerified(refreshed);
        setDone({
          status: result.status,
          studentName: `${student?.first_name ?? ""} ${
            student?.last_name ?? ""
          }`.trim(),
        });
        choose({ step: "done" }, true);
      })
      .catch((e: ApiError) => {
        if (e.status === 401) {
          resetPhone(verified.phone);
          setError("Your code has expired. Ask for a new code.");
        } else if (e.status === 409) {
          setNotice(
            errorText(e, "That time was just taken. Pick another time.")
          );
          setPicked(undefined);
          choose({ step: undefined, start: undefined }, true);
          loadSlots();
        } else {
          setError(errorText(e, "That didn't work. Try again."));
        }
      })
      .finally(() => setBusy(false));
  };

  const bookAnother = () => {
    setDone(undefined);
    setPicked(undefined);
    setDay(undefined);
    choose({ step: undefined, start: undefined }, true);
    loadSlots();
  };

  if (step === "done" && done && summary) {
    return (
      <PublicShell width="narrow">
        <Link
          to={orgHref(org.id)}
          className="flex items-center gap-3 px-5 pt-5 text-base font-semibold text-neutral-900 no-underline hover:underline"
        >
          <OrgLogo
            url={page.photo_url}
            name={org.name}
            size={48}
            maxWidth={200}
            accent={accent}
          />
          {org.name}
        </Link>
        <BookingDone
          summary={summary}
          status={done.status}
          studentName={done.studentName}
          org={org}
          accent={accent}
          onBookAnother={bookAnother}
        />
      </PublicShell>
    );
  }

  const details = step === "details" && !!summary;
  const when = summary && whenLines(summary);
  const embed = inEmbed();
  // The company's whole logo heads the card, a link back to its page; the
  // tutor's photo (or initials) and name sit under it. Without a logo, the
  // company's initials and name.
  const hasLogo = !!safePhoto(page.photo_url);

  // The left pane: who, and the choices this org lets families make.
  const left: ReactNode = (
    <>
      <Link
        to={orgHref(org.id)}
        className="flex items-center gap-2 text-sm font-semibold text-neutral-900 no-underline hover:underline"
      >
        <span className="md:hidden" style={{ color: accent }}>
          <ArrowLeftIcon />
        </span>
        <OrgLogo
          url={page.photo_url}
          name={org.name}
          size={hasLogo ? 48 : 22}
          maxWidth={240}
          accent={accent}
        />
        {!hasLogo && org.name}
      </Link>
      <div className="mt-6 flex items-center gap-4 md:block">
        <PersonPhoto
          url={tutorPage.photo_url}
          name={tutorName}
          size={72}
          accent={accent}
        />
        <div>
          <h1 className="text-2xl font-bold md:mt-3">
            {details ? summary?.title : tutorName}
          </h1>
          {!details && subjects.length === 1 && (
            <p className="text-sm text-gray-500 md:hidden">
              {subjects[0].name}
            </p>
          )}
        </div>
      </div>

      {details && when ? (
        <div className="mt-4 flex flex-col gap-2.5">
          <FactRow icon={<CalendarIcon />}>{when.day}</FactRow>
          <FactRow icon={<ClockIcon />}>
            {when.time} · {lengthLabel(length)}
          </FactRow>
          <FactRow
            icon={
              place?.location_type ===
              OrganizationMeetingLocationType.ONLINE ? (
                <VideoIcon />
              ) : (
                <SewingPinIcon />
              )
            }
          >
            {placeLabel(place)}
          </FactRow>
          <FactRow icon={<GlobeIcon />}>{zoneLabel(zone)}</FactRow>
          <button
            type="button"
            onClick={() => choose({ step: undefined }, true)}
            className="mt-2 flex items-center gap-1.5 self-start text-sm font-semibold"
            style={{ color: accent }}
          >
            <ArrowLeftIcon /> Change time
          </button>
        </div>
      ) : (
        <>
          {tutorPage.bio && (
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">
              <span className={moreBio ? "" : "line-clamp-3"}>
                {tutorPage.bio}
              </span>
              {!moreBio && tutorPage.bio.length > 140 && (
                <button
                  type="button"
                  onClick={() => setMoreBio(true)}
                  className="font-semibold"
                  style={{ color: accent }}
                >
                  More
                </button>
              )}
            </p>
          )}
          <hr className="my-5 border-gray-200" />
          {subjects.length > 1 && (
            <div>
              <label
                htmlFor="subject"
                className="text-xs font-semibold text-gray-600"
              >
                Subject
              </label>
              <select
                id="subject"
                value={subjectId}
                onChange={(e) => choose({ subject: Number(e.target.value) })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5"
              >
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {booking.families_choose_duration ? (
            <ChoiceChips
              label="Length"
              name="length"
              options={booking.duration_options.map((m) => ({
                value: m,
                label: lengthLabel(m),
              }))}
              value={length}
              onChange={(m) => {
                choose({ length: m });
                setPicked(undefined);
              }}
              accent={accent}
            />
          ) : null}
          {booking.families_choose_location ? (
            <ChoiceChips
              label="Where"
              name="place"
              // Once a time is picked, only the places free then.
              options={(picked ? freePlaces(picked) : booking.locations).map(
                (l) => ({
                  value: l.id,
                  label: l.name,
                })
              )}
              value={place?.id ?? 0}
              onChange={(id) => choose({ place: id })}
              accent={accent}
            />
          ) : null}
          <div className="mt-4 flex flex-col gap-2">
            {!booking.families_choose_duration && (
              <FactRow icon={<ClockIcon />}>{lengthLabel(length)}</FactRow>
            )}
            {!booking.families_choose_location && (
              <FactRow
                icon={
                  place?.location_type ===
                  OrganizationMeetingLocationType.ONLINE ? (
                    <VideoIcon />
                  ) : (
                    <SewingPinIcon />
                  )
                }
              >
                {placeLabel(place)}
              </FactRow>
            )}
          </div>
        </>
      )}
    </>
  );

  const dayTimes = day ? byDay.get(day) ?? [] : [];

  // The right pane: the month and the day's times, or the details step.
  const right: ReactNode = details ? (
    <div className="md:max-w-[600px]">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-bold">Your details</h2>
        <span className="text-sm text-gray-500">Step 2 of 3</span>
      </div>
      <div className="mt-5">
        <PhoneVerifyStep
          key={phoneStep.key}
          orgId={org.id}
          accent={accent}
          verified={verified}
          initialPhone={phoneStep.prefill}
          onVerified={(v) => {
            saveVerified(org.id, v);
            setVerified(v);
            setError(undefined);
          }}
          onChangeNumber={() => resetPhone("")}
        />
      </div>
      {verified && (
        <WhoStep
          key={verified.token}
          org={org}
          verified={verified}
          returningOnly={booking.returning_families_only}
          accent={accent}
          busy={busy}
          onBook={book}
        />
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  ) : (
    <div>
      <h2 className="text-xl font-bold">Select a date and time</h2>
      {notice && (
        <p
          role="alert"
          className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          {notice}
        </p>
      )}
      <div className="mt-5 flex flex-col gap-8 lg:flex-row">
        <div className="lg:w-[420px]">
          <div className="flex items-center justify-between lg:justify-center lg:gap-8">
            <button
              type="button"
              aria-label="Previous month"
              disabled={month <= firstMonth}
              onClick={() => shiftMonth(-1)}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 disabled:opacity-30"
            >
              <ChevronLeftIcon />
            </button>
            <span className="font-semibold">
              {moment(monthDate).format("MMMM YYYY")}
            </span>
            <button
              type="button"
              aria-label="Next month"
              disabled={!!lastMonth && month >= lastMonth}
              onClick={() => shiftMonth(1)}
              className="flex h-9 w-9 items-center justify-center rounded-full disabled:opacity-30"
              style={{ backgroundColor: tint(accent), color: accent }}
            >
              <ChevronRightIcon />
            </button>
          </div>
          <MonthGrid
            month={month}
            today={moment.tz(zone).format("YYYY-MM-DD")}
            picked={day}
            isOpen={(date) => byDay.has(date)}
            onPick={(date) => {
              setDay(date);
              setPicked(undefined);
              setNotice(undefined);
            }}
            accent={accent}
          />
          {times === undefined && (
            <p className="mt-2 text-sm text-gray-500">Loading open times…</p>
          )}
          {times?.length === 0 && (
            <p className="mt-2 text-sm text-gray-500">
              No open times this month.
            </p>
          )}
          <label
            className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-gray-600"
            htmlFor="zone"
          >
            <GlobeIcon className="text-gray-400" />
            Time zone
          </label>
          <div className="mt-1.5 max-w-xs text-sm">
            <ZonePicker inputId="zone" value={zone} onChange={setZone} />
          </div>
        </div>

        {day && (
          <div className={`lg:w-[220px] ${embed ? "" : "pb-24 lg:pb-0"}`}>
            <p className="font-semibold">
              {moment(day).format("dddd, MMMM D")}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-2 lg:flex lg:max-h-[520px] lg:flex-col lg:overflow-y-auto lg:pr-1">
              {dayTimes.map((iso) =>
                picked === iso ? (
                  <div
                    key={iso}
                    className="col-span-1 flex gap-2 lg:col-span-1"
                  >
                    <span className="flex flex-1 items-center justify-center rounded-lg bg-gray-600 py-3 font-semibold text-white">
                      {timeLabel(iso, zone)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toDetails(iso)}
                      className="hidden flex-1 rounded-lg py-3 font-semibold text-white lg:block"
                      style={{ backgroundColor: accent }}
                    >
                      Next
                    </button>
                  </div>
                ) : (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setPicked(iso)}
                    className="rounded-lg border py-3 font-semibold"
                    style={{ borderColor: accent, color: accent }}
                  >
                    {timeLabel(iso, zone)}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>

      {/* On a phone, the picked time and Next stay at the bottom. A widget
          can't pin to the website's screen, so there they follow the times. */}
      {picked && (
        <div
          className={`flex items-center justify-between gap-3 bg-white lg:hidden ${
            embed
              ? "mt-5 rounded-lg border border-gray-200 px-4 py-3"
              : "fixed inset-x-0 bottom-0 border-t border-gray-200 px-5 py-4"
          }`}
        >
          <div className="min-w-0">
            <p className="font-semibold">
              {moment.tz(picked, zone).format("ddd, MMM D")} ·{" "}
              {timeLabel(picked, zone)}
            </p>
            <p className="truncate text-xs text-gray-500">
              {subject?.name} with {tutor.first_name} · {lengthLabel(length)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => toDetails(picked)}
            className="shrink-0 rounded-lg px-8 py-3 font-semibold text-white"
            style={{ backgroundColor: accent }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  return (
    <PublicShell width="wide">
      <div className="flex flex-col md:min-h-[640px] md:flex-row">
        <aside className="border-b border-gray-200 p-5 md:w-[330px] md:shrink-0 md:border-b-0 md:border-r md:p-7">
          {left}
        </aside>
        <main className="flex-1 p-5 md:p-8">{right}</main>
      </div>
    </PublicShell>
  );
}
